import {useSyncExternalStore} from 'react';

// Module-scoped filter store (global-filter-store-pattern): the Filter button with its
// active-count badge, the filter drawer and the list hook observe one filter. Fields are
// ProductFeaturesFilterInput's: status (ProductFeatureStatus) and category (exact match).
// The server applies them as a filter AFTER `limit`, so a filtered page can come back short
// while a nextToken remains; Load More reaches the rest.
//
// The list opens on Active features, as it always has. Clear removes that too.
//
// useSyncExternalStore, not useState(globalFilters) + an effect-registered listener: the
// old shape could miss a change fired between a consumer's first render and its effect
// commit (FNP-605). getSnapshot returns the same reference until a change swaps it.
export const DEFAULT_PRODUCT_FEATURES_FILTER: Record<string, string> = {status: 'Active'};

let globalFilters: Record<string, string> = DEFAULT_PRODUCT_FEATURES_FILTER;
const listeners: Set<() => void> = new Set();

const notify = (filters: Record<string, string>) => {
    globalFilters = filters;
    listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getSnapshot = () => globalFilters;

// Test-only: the store outlives a test case, so tests reset it or leak state.
export const __resetProductFeaturesFilterForTesting = (filters = DEFAULT_PRODUCT_FEATURES_FILTER) => {
    notify(filters);
};

export const useProductFeaturesFilter = () => {
    const filters = useSyncExternalStore(subscribe, getSnapshot);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notify(newFilters);
    };

    const handleClearFilters = () => {
        notify({});
    };

    return {
        filters,
        activeCount: Object.keys(filters).length,
        handleSetFilters,
        handleClearFilters,
    };
};
